import type { TransactionCurrency } from '@contracts/payment/Currency';
import type {
  TransactionStatus,
  TransactionType,
} from '@contracts/payment/TransactionResult';
import type { Timestamp } from '@src/common/time/Timestamp';

export interface TransactionAttributes {
  id: string;
  type: TransactionType;
  status: TransactionStatus;
  date: Timestamp;
  transactionId: string;
  client: {
    id: string;
    name: string;
  };
  card: string;
  amount: number;
  currency: TransactionCurrency;
  children: TransactionAttributes[];
  errorReason?: string;
  errorCode?: string;
}

export class Transaction {
  readonly id: string;
  readonly type: TransactionType;
  readonly status: TransactionStatus;
  readonly date: Timestamp;
  readonly transactionId: string;
  readonly client: {
    id: string;
    name: string;
  };
  readonly card: string;
  readonly amount: number;
  readonly currency: TransactionCurrency;
  readonly children: Transaction[];
  readonly errorReason?: string;
  readonly errorCode?: string;

  constructor(attributes: TransactionAttributes) {
    this.id = attributes.id;
    this.type = attributes.type;
    this.status = attributes.status;
    this.date = attributes.date;
    this.transactionId = attributes.transactionId;
    this.client = attributes.client;
    this.card = attributes.card;
    this.amount = attributes.amount;
    this.currency = attributes.currency;
    this.children = attributes.children.map((child) => new Transaction(child));
    this.errorReason = attributes.errorReason;
    this.errorCode = attributes.errorCode;
  }

  get error() {
    if (!this.errorReason && !this.errorCode) {
      return;
    }
    if (this.errorReason && this.errorCode) {
      return `[Code: ${this.errorCode}] ${this.errorReason}`;
    }
    if (this.errorReason) {
      return this.errorReason;
    }
    if (this.errorCode) {
      return `Code: ${this.errorCode}`;
    }
  }

  canBeSettledBy(clientId: string) {
    return (
      this.isPreauth() &&
      !this.isPaid() &&
      !this.hasError() &&
      this.isCreatedBy(clientId)
    );
  }

  isCreatedBy(clientId: string) {
    return clientId === this.client.id;
  }

  isSuccessfullyCreatedBy(clientId: string) {
    return this.isCreatedBy(clientId) && this.isSuccessful();
  }

  isPaid(): boolean {
    const transactionPaid =
      (this.isPayment() || this.isSettlement()) && this.isSuccessful();
    return transactionPaid || this.children.some((child) => child.isPaid());
  }

  private isSuccessful(): boolean {
    return this.status === 'SUCCESS';
  }

  private isSettlement(): boolean {
    return this.type === 'SETTLEMENT';
  }

  private isPayment(): boolean {
    return this.type === 'PAYMENT';
  }

  private isPreauth(): boolean {
    return this.type === 'PREAUTHORIZATION';
  }

  isLastOperation() {
    return this.children.length === 0;
  }

  private hasError(): boolean {
    const isInError = this.status !== 'SUCCESS';
    return isInError || this.children.some((child) => child.hasError());
  }
}
